import moment from "moment";

import { CardHeader } from "../cards";
import { Box, Item, List, Text } from "../elements";
import { get_date } from "../generalComponets";
import { CardLayout } from "../v3";

// Mirrors LEAD_UPDATE_FIELD_CONFIG in cowallet-backend/src/crm/pro/crm-pro-lead.service.ts
const FIELD_LABELS = {
  name: "Name",
  contactName: "Contact Name",
  contactPhone: "Contact Phone",
  contactEmail: "Contact Email",
  source: "Source",
  expectedBudget: "Expected Budget",
  expectedStartDate: "Expected Start Date",
  date: "Reference Date",
  siteAddress: "Site Location",
  priority: "Priority",
};

const DATE_FIELDS = ["expectedStartDate", "date"];

const ACTIVITY_TYPE_CONFIG = {
  CREATED: { label: "Created", color: "#2E7D32", bg: "#E8F5E9" },
  UPDATED: { label: "Updated", color: "#1565C0", bg: "#E3F2FD" },
  PRIORITY_CHANGE: { label: "Priority Change", color: "#674BF5", bg: "#EDE9FD" },
  TASK_CREATED: { label: "Task Created", color: "#2E7D32", bg: "#E8F5E9" },
  TASK_UPDATED: { label: "Task Updated", color: "#1565C0", bg: "#E3F2FD" },
  TASK_DELETED: { label: "Task Deleted", color: "#C62828", bg: "#FDECEA" },
  TASK_PROGRESS_UPDATED: { label: "Task Progress", color: "#EF6C00", bg: "#FFF3E0" },
  NOTE_ADDED: { label: "Note Added", color: "#6A4A00", bg: "#FFF8E1" },
  DOCUMENT_UPLOADED: { label: "Document Uploaded", color: "#00695C", bg: "#E0F2F1" },
};

const getActivityTypeConfig = (type) => ACTIVITY_TYPE_CONFIG[type] || { label: type, color: "#616161", bg: "#F0F0F0" };

const formatChangeValue = (field, value) => {
  if (value === null || value === undefined || value === "") return "None";

  if (field === "priority") return value?.name || "None";

  if (field === "siteAddress") {
    const parts = [value?.locality, value?.city, value?.state].filter(Boolean);
    return parts.length ? parts.join(", ") : "None";
  }

  if (DATE_FIELDS.includes(field)) {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD MMM YYYY") : "None";
  }

  return String(value);
};

// One "Field: old → new" row, rendered richly rather than as a single flat sentence.
const ChangeRow = ({ label, oldValue, newValue }) => (
  <Box style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 4, lineHeight: 1.6 }}>
    <Text as="span" style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>
      {label}
    </Text>
    <Text as="span" style={{ fontSize: 12, color: "#9E9E9E", textDecoration: "line-through" }}>
      {oldValue}
    </Text>
    <Text as="span" style={{ fontSize: 12, color: "#BDBDBD" }}>
      →
    </Text>
    <Text as="span" style={{ fontSize: 12, fontWeight: 600, color: "#674BF5" }}>
      {newValue}
    </Text>
  </Box>
);

const TASK_STATUS_BADGE = {
  DONE: { label: "Done", color: "#2E7D32", bg: "#E8F5E9" },
  INPROGRESS: { label: "In Progress", color: "#EF6C00", bg: "#FFF3E0" },
  TODO: { label: "Todo", color: "#1565C0", bg: "#E3F2FD" },
};

// TASK_PROGRESS_UPDATED metadata is a snapshot ({status, notes?}), not a before/after diff —
// status shows as a badge, notes as text. Attachment presence is conveyed in the description
// text itself ("... with an image/document") — the file's URL is a short-lived signed token,
// never stored or rendered here.
const TaskProgressDetails = ({ metadata }) => {
  const statusEntry = metadata.find((m) => m.field === "status");
  const notesEntry = metadata.find((m) => m.field === "notes");
  const statusBadge =
    statusEntry && (TASK_STATUS_BADGE[statusEntry.newValue] || { label: statusEntry.newValue, color: "#616161", bg: "#F0F0F0" });

  return (
    <Box style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {statusBadge && (
        <Text
          as="span"
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: statusBadge.color,
            backgroundColor: statusBadge.bg,
            padding: "2px 8px",
            borderRadius: 10,
            width: "fit-content",
          }}
        >
          {statusBadge.label}
        </Text>
      )}

      {notesEntry?.newValue && (
        <Text as="small" style={{ fontSize: 12, color: "#555" }}>
          {notesEntry.newValue}
        </Text>
      )}
    </Box>
  );
};

export const LeadActivityCard = ({ title, style, activities }) => {
  const activityCount = activities?.length || 0;

  return (
    <CardLayout sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <CardHeader title={title}>
        <Text
          as="span"
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#674BF5",
            backgroundColor: "#EDE9FD",
            padding: "3px 10px",
            borderRadius: 12,
            whiteSpace: "nowrap",
          }}
        >
          {activityCount} {activityCount === 1 ? "update" : "updates"}
        </Text>
      </CardHeader>

      <List className="mc-activity-card-list thin-scrolling" style={{ overflowY: "auto", flex: 1, minHeight: 0, ...style }}>
        {activities?.map((item) => {
          const fullName = [item.createdBy?.firstName, item.createdBy?.lastName].filter(Boolean).join(" ");
          const typeConfig = getActivityTypeConfig(item.type);
          const isTaskProgress = item.type === "TASK_PROGRESS_UPDATED";

          // Structured field diffs (lead update / priority change) are computed from metadata —
          // description is only a fallback for activity types that carry no metadata at all.
          const changes =
            item.metadata?.length && !isTaskProgress
              ? item.metadata.map((change) => ({
                  label: FIELD_LABELS[change.field] || change.field,
                  oldValue: formatChangeValue(change.field, change.oldValue),
                  newValue: formatChangeValue(change.field, change.newValue),
                }))
              : [];

          return (
            <Item key={item.id} className="mc-activity-card-item">
              {/* Top line */}
              <Box>
                {(isTaskProgress || changes.length === 0) && (
                  <Text as="small" style={{ display: "block", marginBottom: isTaskProgress ? 4 : 0 }}>
                    {item.description}
                  </Text>
                )}

                {isTaskProgress && item.metadata?.length ? (
                  <TaskProgressDetails metadata={item.metadata} />
                ) : changes.length > 0 ? (
                  <Box style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {changes.map((change, idx) => (
                      <ChangeRow key={idx} {...change} />
                    ))}
                  </Box>
                ) : null}
              </Box>

              {/* Meta info */}
              <Box className="mc-activity-card-body">
                {/* Type badge + actor */}
                <Box style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <Text
                    as="span"
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: typeConfig.color,
                      backgroundColor: typeConfig.bg,
                      padding: "2px 8px",
                      borderRadius: 10,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {typeConfig.label}
                  </Text>
                  <Text className="mc-activity-card-text" style={{ margin: 0 }}>
                    by {fullName || "Unknown"}
                  </Text>
                </Box>

                {/* Timestamp */}
                <Text className="mc-activity-card-text" style={{ fontSize: 10 }}>
                  {get_date(item.createdAt, "DD MMM YYYY HH:mm a")}
                </Text>
              </Box>
            </Item>
          );
        })}
      </List>
    </CardLayout>
  );
};
